/**
 * useLivekitToken Hook (Phase 2)
 *
 * Fetches a LiveKit token from the Phase 2 endpoint
 *   `GET /api/livekit/token?sessionId={id}`
 *
 * The endpoint returns 503 when LiveKit is not configured server-side and 401
 * when the caller is not authenticated. Both are surfaced as a clean error
 * status so that callers can choose to hide LiveKit UI gracefully instead of
 * propagating an exception.
 *
 * Mirrors the data-fetching style of `use-sso-config.ts` (TanStack Query +
 * `staleTime`/`retry` knobs) so the cached result is shared across panes that
 * mount the hook for the same session — e.g. the presenter page and the join
 * page in the same window.
 */
import { resolveApiBaseUrl } from '@polygraf/shared'
import { useQuery } from '@tanstack/react-query'

const API_URL = resolveApiBaseUrl(import.meta.env.VITE_API_URL)

export type LivekitTokenStatus = 'idle' | 'loading' | 'ready' | 'error'

export interface LivekitTokenResult {
	token: string | null
	url: string | null
	room: string | null
	status: LivekitTokenStatus
	/** Human-readable error message; `null` when status !== 'error'. */
	error: string | null
	/** True when the failure is a 503 (LiveKit disabled in this environment). */
	disabled: boolean
	/** True when the failure is a 401 (caller not authenticated). */
	unauthenticated: boolean
	refetch: () => void
}

interface TokenResponse {
	token: string
	url: string
	room: string
}

class LivekitTokenError extends Error {
	readonly status: number
	constructor(message: string, status: number) {
		super(message)
		this.name = 'LivekitTokenError'
		this.status = status
	}
}

async function fetchToken(sessionId: string): Promise<TokenResponse> {
	// `credentials: 'include'` is required because Better Auth uses cookie-
	// based sessions; otherwise the server treats us as anonymous and 401s.
	const url = `${API_URL}/api/livekit/token?sessionId=${encodeURIComponent(sessionId)}`
	const res = await fetch(url, {
		method: 'GET',
		credentials: 'include',
		headers: { Accept: 'application/json' },
	})

	if (!res.ok) {
		// Try to surface the server's error message but never let a parse
		// failure mask the underlying HTTP status — fall back to a generic
		// message keyed off the status code.
		let message = `Token request failed: ${res.status}`
		try {
			const body = (await res.json()) as { error?: string }
			if (body?.error) message = body.error
		} catch {
			// JSON parse failed — keep the generic message.
		}
		throw new LivekitTokenError(message, res.status)
	}

	return (await res.json()) as TokenResponse
}

interface UseLivekitTokenOptions {
	/** When `false`, the hook stays idle. Use to gate on `livekitEnabled`. */
	enabled?: boolean
}

export function useLivekitToken(
	sessionId: string | null | undefined,
	options: UseLivekitTokenOptions = {},
): LivekitTokenResult {
	const enabled = options.enabled !== false && !!sessionId

	const query = useQuery<TokenResponse, LivekitTokenError>({
		queryKey: ['livekit-token', sessionId],
		queryFn: () => fetchToken(sessionId as string),
		enabled,
		// Tokens are valid for an hour server-side. 50min keeps a comfortable
		// margin and avoids stampeding the auth check on every focus.
		staleTime: 50 * 60 * 1000,
		// Don't retry on 401/503 — those are deterministic, not transient.
		retry: (failureCount, error) => {
			if (error instanceof LivekitTokenError) {
				if (error.status === 401 || error.status === 503) return false
			}
			return failureCount < 1
		},
		refetchOnWindowFocus: false,
	})

	if (!enabled) {
		return {
			token: null,
			url: null,
			room: null,
			status: 'idle',
			error: null,
			disabled: false,
			unauthenticated: false,
			refetch: () => query.refetch(),
		}
	}

	if (query.isLoading || query.isFetching) {
		return {
			token: null,
			url: null,
			room: null,
			status: 'loading',
			error: null,
			disabled: false,
			unauthenticated: false,
			refetch: () => query.refetch(),
		}
	}

	if (query.isError || !query.data) {
		const err = query.error
		const status = err instanceof LivekitTokenError ? err.status : 0
		return {
			token: null,
			url: null,
			room: null,
			status: 'error',
			error: err?.message ?? 'Failed to fetch LiveKit token',
			disabled: status === 503,
			unauthenticated: status === 401,
			refetch: () => query.refetch(),
		}
	}

	return {
		token: query.data.token,
		url: query.data.url,
		room: query.data.room,
		status: 'ready',
		error: null,
		disabled: false,
		unauthenticated: false,
		refetch: () => query.refetch(),
	}
}
